<template>
    <tailwind-layout title="User Transactions">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden lg:flex justify-between items-center"> <!-- Block for lg and larger -->
                <h1 class="font-semibold text-2xl leading-tight">{{ user.name }} Transactions</h1>
                <div class="flex items-center space-x-4">
                    <inertia-link class="whiteButton" :href="backUrl">
                        <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                    <a target="_blank" :href="route('rented-properties.transactions.user.download', [user.id, form])"
                       class="whiteButton">
                        <font-awesome-icon icon="fa-download" class="pr-2" /> Download
                    </a>
                </div>
            </div>

            <div class="mb-3 px-6 lg:px-8 mx-auto flex flex-col lg:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex justify-between items-center">
                    <h1 class="font-semibold text-2xl leading-tight">{{ user.name }} Transactions</h1>
                    <a target="_blank" :href="route('rented-properties.transactions.user.download', [user.id, form])"
                       class="whiteButton">
                        <font-awesome-icon icon="fa-download" class="pr-2" /> Download
                    </a>
                </div>
            </div>

        </template>
        <div class="overflow-hidden">
            <div class="relative">
                <div class="grid grid-cols-2 gap-4 mb-6 sm:flex sm:justify-items-stretch order-1">
                    <div class="sm:w-2/12 pr-4 order-1 sm:order-1 pt-5">
                        <select v-model="form.paginate" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md" aria-label="Number of items per page">
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div class="sm:w-2/12 sm:pr-4 order-3 sm:order-2">
                        <label for="date_from" class="block text-sm font-medium text-gray-700">
                            Date From
                        </label>
                        <input id="date_from" v-model="form.date_from" type="date" class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md">
                    </div>
                    <div class="sm:w-2/12 sm:pr-4 order-4 sm:order-3">
                        <label for="date_to" class="block text-sm font-medium text-gray-700">
                            Date To
                        </label>
                        <input id="date_to" v-model="form.date_to" type="date" class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md">
                    </div>
                    <div class="sm:w-4/12 sm:pr-4 order-2 sm:order-4">
                        <label for="property" class="block text-sm font-medium text-gray-700">
                            Property
                        </label>
                        <select id='property' v-model="form.property" class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm">
                            <option value="0">All</option>
                            <option v-for="property in properties" :value="property.id"
                                    :key="property.id">{{ property.full_address }}
                            </option>
                        </select>
                    </div>

                </div>
            </div>
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden lg:border">
                                <input ref="sort_by" type="hidden" v-model="form.sort_by" />
                                <input ref="sort_direction" type="hidden" v-model="form.sort_direction" />
                                <table class="min-w-full divide-y divide-gray-300">
                                    <thead class="bg-blue-50">
                                    <tr>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            <span @click="sortTable('amount')">
                                                Amount
                                                <icon v-show="form.sort_by === 'amount'" :name="form.sort_direction_icon" />
                                                <icon v-if="!form.sort_by || form.sort_by !== 'amount'" :name="'sort'" />
                                            </span>
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Convenience Fee
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            {{$page.props.user.role === 'agent' ? 'My Payout' : 'Total'}}
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            <span @click="sortTable('created_at')">
                                                Date
                                                <icon v-show="form.sort_by === 'created_at'" :name="form.sort_direction_icon" />
                                                <icon v-if="!form.sort_by || form.sort_by !== 'created_at'" :name="'sort'" />
                                            </span>
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Detail
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Lease Term
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Property
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            {{$page.props.user.role === 'agent' ? 'Tenant' : 'Landlord'}}
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>

                                    <tr v-for="(transaction, index) in transactions.data" :key="index"
                                           :class="[(index % 2 === 0 ? 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer' : 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer')]"
                                    >
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            ${{ transaction.amount }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            ${{ transaction.stripe_fee }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            ${{ transaction.total }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ transaction.date }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            <ul v-if="transaction.payments.length > 1" class="mt-3 list-inside text-sm">
                                                <li v-for="(payment, key) in transaction.payments" :key="key">
                                                    {{ payment.month !== null ? payment.month.month_name : payment.one_time_charge.title }}
                                                </li>
                                            </ul>
                                            <span v-else>
                                                {{ transaction.payments[0].month !== null ? transaction.payments[0].month.month_name : transaction.payments[0].one_time_charge.title }}
                                            </span>
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ transaction.lease_term ? transaction.lease_term.start_end : '' }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ transaction.property ? transaction.property.full_address : '' }}
                                        </td>
                                        <td v-if="$page.props.user.role === 'agent'" class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{transaction.tenant ? transaction.tenant.name : ''}}
                                        </td>
                                        <td v-else-if="$page.props.user.role === 'applicant'" class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{transaction.agent ? transaction.agent.name : ''}}
                                        </td>
                                        <td v-else class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                        </td>
                                    </tr>
                                    <tr v-show="!transactions.data.length">
                                        <td colspan="10" class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                            No transactions were found.
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <pagination class="mt-6" :links="transactions.meta.links" :results="transactions.meta" :type="'transactions'"/>
        </div>
    </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons'

export default {
    components: {
        Icon,
        Pagination,
        TailwindLayout,
        FontAwesomeIcon
    },
    props: {
        filters: Object,
        transactions: Object,
        user: Object,
        properties: Object,
    },
    data() {
        return {
            form: {
                property: this.filters.property,
                date_from: this.filters.date_from,
                date_to: this.filters.date_to,
                search: this.filters.search,
                trashed: this.filters.trashed,
                paginate: (this.filters.paginate)?this.filters.paginate:25,
                sort_by: this.filters.sort_by,
                sort_direction_icon : (this.filters.sort_direction === 'desc')?'sort-up':'sort-down'
            },
            faChevronLeft,
        }
    },
    computed: {
        backUrl() {
            const urlParams = new URLSearchParams(window.location.search)
            const propertyId = urlParams.get('property_id')
            if (propertyId) {
                return this.route('properties.show', propertyId) + '?tab=rent-payments'
            }
            return this.route('rented-properties.index')
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function() {
                const urlParams = new URLSearchParams(window.location.search)
                const propertyId = urlParams.get('property_id')
                let url = this.route('rented-properties.transactions.user', this.user.id)
                if (propertyId) {
                    url += '?property_id=' + propertyId
                }
                this.$inertia.get(url, pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    methods: {
        sortTable(column) {
            this.form.sort_by = column
            this.form.sort_direction = (this.form.sort_direction === 'desc')?'asc':'desc'
            this.form.sort_direction_icon = (this.form.sort_direction === 'desc')?'sort-up':'sort-down'
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
    },
}
</script>
